import { enhancePreparedOcrImage, type PreparedOcrImage } from '@/core/image'
import type { AttributeMetric, OcrFormatRecovery } from '@/domain/types'
import { parseExpText, parseJobName, parseResource } from './parsers'
import { parseLevelDigits } from './level-image'

export const attributeMetrics = ['level', 'jobName', 'exp', 'hp', 'mp'] as const
export function isAttributeMetric(metric: string): metric is AttributeMetric {
  return (attributeMetrics as readonly string[]).includes(metric)
}

export type ClassicTextRecovery = { kind: 'unchanged' | 'rejected' }
  | { kind: 'recovered'; text: string; reasons: OcrFormatRecovery[] }

/** Only call after identifying a complete numeric field; never normalize labels or boundaries. */
export function normalizeClassicNumericField(text: string): string | null {
  if (!/^(?:[\dSsZzOoIl|]+|[\dSsZzOoIl|]{1,3}(?:,[\dSsZzOoIl|]{3})+)$/.test(text)) return null
  const normalized = text.replace(/[Ss]/g, '5').replace(/[Zz]/g, '2').replace(/[Oo]/g, '0').replace(/[Il|]/g, '1')
  return Number.isSafeInteger(Number(normalized.replaceAll(',', ''))) ? normalized : null
}

export function recoverClassicResourceText(metric: 'hp' | 'mp', text: string): ClassicTextRecovery {
  const normalized = text.normalize('NFKC').trim().replace(/^[\p{Script=Han}\s:：]+/u, '')
  const match = /^(?:(HP|MP|DF)\s*[:：]?\s*)?(\[)?\s*([^\[\]\/]+?)\s*\/\s*([^\[\]\/]+?)\s*(\])?$/i.exec(normalized)
  if (!match || Boolean(match[2]) !== Boolean(match[5])) return { kind: 'rejected' }
  const label = match[1]?.toUpperCase()
  if (label && label !== metric.toUpperCase() && !(metric === 'mp' && label === 'DF')) return { kind: 'rejected' }
  const current = normalizeClassicNumericField(match[3]!), max = normalizeClassicNumericField(match[4]!)
  if (current === null || max === null) return { kind: 'rejected' }
  const reasons: OcrFormatRecovery[] = []
  if (current !== match[3] || max !== match[4]) reasons.push('numeric-lookalike')
  if (match[2]) reasons.push('resource-brackets')
  if (!reasons.length && normalized === text) return { kind: 'unchanged' }
  const parsed = parseResource(`${current}/${max}`)
  if (!parsed || parsed.current! > parsed.max!) return { kind: 'rejected' }
  return { kind: 'recovered', text: `${metric.toUpperCase()} ${current}/${max}`, reasons }
}

/** This adapter is mxdc-only; shared parsers keep their existing rules for other presets. */
export function parseClassicResourceText(metric: 'hp' | 'mp', text: string) {
  const recovered = recoverClassicResourceText(metric, text)
  return recovered.kind === 'rejected' ? null : parseResource(recovered.kind === 'recovered' ? recovered.text : text)
}

export function parseManualText(metric: AttributeMetric, text: string) {
  const value = text.normalize('NFKC').trim().replace(metric === 'jobName'
    ? /^(?:职业|職業)\s*[:：]?\s*/u : /^[\p{Script=Han}\s:：]+/u, '')
  if (metric === 'level') return parseLevelDigits(value.replace(/^LV\.?\s*/i, ''))
  if (metric === 'jobName') return parseJobName(value)
  if (metric === 'exp') {
    const parsed = parseExpText(value)
    return parsed.ambiguous || parsed.value?.absolute == null || parsed.value.ratio == null ? null
      : { ...parsed.value, percentagePrecision: parsed.percentage!.precision, ...(!/^EXP/i.test(value) && parsed.percentage!.precision === 0 ? { percentageRounding: 'floor' as const } : {}) }
  }
  const resourceText = value.replace(/^(?:HP|MP|DF)\s*[:：]?\s*/i, '')
  if (!/^[\dOoIl|,]+\s*\/\s*[\dOoIl|,]+$/.test(resourceText)) return null
  const resource = parseResource(resourceText)
  return resource && resource.current !== null && resource.max !== null && resource.max > 0
    && resource.current <= resource.max ? resource : null
}

export function prepareManualImage(source: PreparedOcrImage, scale = 6): PreparedOcrImage {
  return enhancePreparedOcrImage(source, { scale, padding: 3, contrast: 1, sharpen: 0, interpolation: 'smooth' })
}
